/**
 * Audit Log Service — Firestore queries for the audit log viewer.
 *
 * Required Firestore composite indexes (Firebase console will prompt you
 * with a direct link to create them the first time each query runs):
 *
 *   auditLogs: action ASC, timestamp DESC
 *   auditLogs: audience ASC, timestamp DESC
 *   auditLogs: audience ASC, dayNumber ASC, timestamp DESC
 *
 * Simple orderBy('timestamp', 'desc') without additional where clauses
 * uses the auto-indexed single-field timestamp index — no composite needed.
 */

import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  Timestamp,
  type DocumentSnapshot,
  type QueryConstraint,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { AuditLog, AuditAction, AuditLogFilters } from '@/types';
import type { Audience } from '@/types/auth.types';

// ─── Constants ─────────────────────────────────────────────────────────────────

export const AUDIT_PAGE_SIZE = 25;

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface AuditLogPage {
  items:   AuditLog[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}

// ─── Converter ─────────────────────────────────────────────────────────────────

function docToAuditLog(d: DocumentSnapshot): AuditLog {
  const data = d.data() as Record<string, unknown>;
  const ts   = data.timestamp;
  return {
    id:              d.id,
    userId:          (data.userId          as string) ?? '',
    userDisplayName: (data.userDisplayName as string) ?? 'Unknown',
    userEmail:       (data.userEmail       as string) ?? '',
    action:          (data.action          as AuditAction),
    timestamp:       ts instanceof Timestamp ? ts.toDate() : new Date(),
    audience:        (data.audience    as Audience | null) ?? null,
    monthNumber:     (data.monthNumber as number  | null) ?? null,
    weekNumber:      (data.weekNumber  as number  | null) ?? null,
    dayNumber:       (data.dayNumber   as number  | null) ?? null,
    recordId:        (data.recordId    as string  | null) ?? null,
    fieldsChanged:   (data.fieldsChanged  as string[])              ?? [],
    previousValues:  (data.previousValues as Record<string, unknown>) ?? {},
    newValues:       (data.newValues      as Record<string, unknown>) ?? {},
    metadata:        (data.metadata       as Record<string, unknown>) ?? {},
  };
}

// ─── Paginated list ────────────────────────────────────────────────────────────

export async function getAuditLogs(
  filters: AuditLogFilters,
  cursor:  DocumentSnapshot | null = null,
): Promise<AuditLogPage> {
  const constraints: QueryConstraint[] = [];

  if (filters.action)   constraints.push(where('action',    '==', filters.action));
  if (filters.audience) constraints.push(where('audience',  '==', filters.audience));
  if (filters.dateFrom) constraints.push(where('timestamp', '>=', Timestamp.fromDate(filters.dateFrom)));
  if (filters.dateTo)   constraints.push(where('timestamp', '<=', Timestamp.fromDate(filters.dateTo)));

  constraints.push(orderBy('timestamp', 'desc'));
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(AUDIT_PAGE_SIZE + 1));

  const q    = query(collection(db, 'auditLogs'), ...constraints);
  const snap = await getDocs(q);

  const hasMore = snap.docs.length > AUDIT_PAGE_SIZE;
  const docs    = hasMore ? snap.docs.slice(0, AUDIT_PAGE_SIZE) : snap.docs;

  return {
    items:   docs.map(docToAuditLog),
    lastDoc: docs[docs.length - 1] ?? null,
    hasMore,
  };
}

// ─── Per-day history (used in DayEditorPage) ───────────────────────────────────

export async function getDayAuditLogs(
  audience:  Audience,
  dayNumber: number,
): Promise<AuditLog[]> {
  // Requires composite index: audience ASC, dayNumber ASC, timestamp DESC
  const q = query(
    collection(db, 'auditLogs'),
    where('audience',  '==', audience),
    where('dayNumber', '==', dayNumber),
    orderBy('timestamp', 'desc'),
    limit(15),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => docToAuditLog(d as DocumentSnapshot));
}
